<template>
  <div class="AddCoveragesForm">
    <h2 class="gl_form-title small">
      {{ t('hiring.common.additional-coverages.form-legend') }}
    </h2>

    <h3 class="gl_heading">
      {{ t('hiring.common.additional-coverages.subtitle') }}
    </h3>

    <OptionCardInsuranceComponent
      v-for="coverage in coveragesConfig"
      :key="coverage.code"
      :icon="coverage.icon"
      :title="coverage.label"
      :button="t('hiring.common.insurance-options.view-detail')"
      @open-coverage="openCoverage(coverage.code)"
    />

    <v-divider />

    <InsuranceOptionsForm
      v-for="(coverage, i) in additionalCoverages"
      :id="`insurance-options-form-${i}`"
      :key="i"
      :title="`${coverage.title} (${coverage.date})`"
      :insurance-options="coverage.options"
      @updateOptions="handleUpdateCoverageOptions(i, $event)"
    />

    <ModalComponent v-model:value="modalCoverageVisible">
      <InsuranceDetail
        v-if="currentCoverage && modalCoverageVisible"
        :coverage="currentCoverage as AdditionalCoverageConfig"
      />

      <BaseButton
        type="outlined"
        icon="icon-arrow-left"
        @click="modalCoverageVisible = false"
        :name="gtmPrefix + BACK_BTN"
        :id="gtmPrefix + BACK_BTN"
        :emitGTMEvent="{ event: GTMEventType.BACK }"
      >
        {{ t('rater.base.back') }}
      </BaseButton>
    </ModalComponent>

    <BaseButton
      :ariaDisabled="!isValidForm"
      type="primary"
      @click="submit"
    >
      {{ t('hiring.common.additional-coverages.recalculate-price') }}
    </BaseButton>
  </div>
</template>

<script lang="ts" setup>
  import AdditionalCoverage from '@/modules/common/common/models/APP/AdditionalCoverage'
  import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
  import ModalComponent from '@/modules/core/components/utils/ModalComponent.vue'
  import { useAdditionalCoveragesComposable } from '@/modules/core/composables/hiringAdditionalCoveragesComposable'
  import GTMEventType from '@/modules/core/models/GTMEventType'
  import { BACK_BTN } from '@/modules/core/models/IdHTMLItems'
  import type { CoverageInfoInterface } from '@/modules/core/models/types'
  import InsuranceDetail from '@/modules/health/hiring/components/InsuranceDetail.vue'
  import InsuranceOptionsForm from '@/modules/health/hiring/components/InsuranceOptionsForm.vue'
  import OptionCardInsuranceComponent from '@/modules/health/hiring/components/OptionCardInsuranceComponent.vue'
  import AdditionalCoverageConfig from '@/modules/health/hiring/models/APP/AdditionalCoverageConfig'
  import { computed, inject, onBeforeMount, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import ForeignHealthHiringInsured from '../../models/APP/ForeignHealthHiringInsured'
  import { useForeignHealthStore } from '../../stores'

  const { t } = useI18n()
  const foreignHealthStore = useForeignHealthStore()

  interface PropsInterface {
    answers: Record<string, any>
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})

  const emit = defineEmits(['submit'])

  const validateForm = inject<() => Promise<boolean>>('validateForm') ?? (async () => true)

  const { loadAdditionalCoverages } = useAdditionalCoveragesComposable(props.answers)

  const modalCoverageVisible = ref(false)
  const currentCoverage = ref<AdditionalCoverageConfig | null>(null)
  const coveragesConfig = ref<AdditionalCoverageConfig[]>([])
  const coveragesStore = ref<AdditionalCoverage[][]>([])
  const additionalCoverages = ref<CoverageInfoInterface[]>([])

  const isValidForm = computed(() => {
    //Control en tcontratacion y recontratacion

    let enabledSave = false
    enabledSave = additionalCoverages.value.some((coverage, idx: number) => {
      return coverage.options.some((option) => {
        if (option.checked) {
          const findCoverage = coveragesStore.value[idx].find((cs) => cs.key === option.code)
          if (!findCoverage) return true
        }
        const find = coveragesStore.value[idx].find((cs) => {
          return cs.key === option.code && cs.checked === option.checked
        })
        if (find) return true
      })
      //const coverages = coveragesStore.value[idx]
    })
    return enabledSave
  })

  const gtmPrefix = 'hiring_health_base_'

  function openCoverage(key: string) {
    const coverageToOpen = coveragesConfig.value.find((coverage) => coverage.code === key)
    if (!coverageToOpen) return

    currentCoverage.value = coverageToOpen
    modalCoverageVisible.value = true
  }

  function handleUpdateCoverageOptions(index: number, newOptions: any) {
    additionalCoverages.value[index].options = newOptions
  }

  function getAdditionalCoveragesValues(coveragesInfo: CoverageInfoInterface[]) {
    return coveragesInfo.map((coverage) => {
      const options = coverage.options.filter((c) => c.checked)

      return options.map(
        ({ code, checked, value }) => new AdditionalCoverage({ key: code, checked, value })
      )
    })
  }

  async function submit() {
    const insuredAdditionalCoverages = getAdditionalCoveragesValues(additionalCoverages.value)

    const insureds: ForeignHealthHiringInsured[] = props.answers.insureds
    const insuredData = insureds.map((item, i) => {
      item.additionalCoverages = insuredAdditionalCoverages[i]

      return item
    })
    const isValid = await validateForm()
    if (!isValid) return

    emit('submit', insuredData)
  }

  function beforeMount() {
    coveragesConfig.value = foreignHealthStore.getAdditionalCoveragesConfig()
    coveragesStore.value = props.answers.insureds.map((i: ForeignHealthHiringInsured) => {
      return i.additionalCoverages
    })
    additionalCoverages.value = loadAdditionalCoverages(
      coveragesConfig.value as AdditionalCoverageConfig[]
    )
  }

  onBeforeMount(beforeMount)
</script>

<style lang="scss" scoped>
  .AddCoveragesForm {
    .v-divider {
      margin: $spacing-l 0;
    }

    .OptionCardInsuranceComponent:not(:last-child) {
      margin-bottom: $spacing-s;
    }
  }
</style>
